import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from '@web-stories-wp/react';
import { __, sprintf } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Text,
  TextArea,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';
import {
  createLottieTextRowsUpdate,
  getLottieTextRowsConfig,
  getLottieTextRowsMessage,
  getLottieTextRowsSource,
  getLottieTextRowsValue,
  loadLottieTextRowsMeasurer,
  resetLottieTextRows,
  validateLottieTextRows,
} from '../../../../elements/lottie/textRows';
import { inputContainerStyleOverride } from '../../shared';

const CopyMessage = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
  line-height: 16px;

  &[role='alert'] {
    color: ${({ theme }) => theme.colors.fg.negative};
  }
`;

function MultiLineCopyInput({ element, pushUpdate }) {
  const config = getLottieTextRowsConfig(element);
  const source = getLottieTextRowsSource(element);
  const [copy, setCopy] = useState(source);
  const [measurement, setMeasurement] = useState({ status: 'pending' });
  const fontKey = JSON.stringify({ rows: config.rows, fonts: element.fonts });
  const rows = useMemo(() => JSON.parse(fontKey).rows, [fontKey]);

  useEffect(() => {
    setCopy(source);
  }, [source, element.id]);

  useEffect(() => {
    let cancelled = false;
    let measurer;
    setMeasurement({ status: 'pending' });
    const { rows, fonts = [] } = JSON.parse(fontKey);

    loadLottieTextRowsMeasurer(rows, fonts)
      .then((loadedMeasurer) => {
        measurer = loadedMeasurer;
        if (cancelled) {
          measurer.destroy();
          return;
        }
        setMeasurement({ status: 'ready', measurer });
      })
      .catch(() => {
        if (!cancelled) {
          setMeasurement({ status: 'font-error' });
        }
      });

    return () => {
      cancelled = true;
      measurer?.destroy();
    };
  }, [fontKey]);

  const layout = useMemo(() => {
    if (measurement.status !== 'ready') {
      return { status: measurement.status };
    }
    try {
      return validateLottieTextRows(
        copy,
        rows,
        measurement.measurer.measure,
        config.maxLength
      );
    } catch {
      return { status: 'font-error' };
    }
  }, [copy, measurement, rows, config.maxLength]);

  const commit = useCallback(
    (nextCopy, nextLayout) => {
      pushUpdate(
        (current) =>
          current.id === element.id
            ? createLottieTextRowsUpdate(current, nextCopy, nextLayout)
            : {},
        true
      );
    },
    [element.id, pushUpdate]
  );

  // Resolve pending drafts when fonts arrive, including restored history entries.
  useEffect(() => {
    if (measurement.status === 'pending' || copy !== source) {
      return;
    }
    const draft = element.definition.meta.storytellerTextRowsDraft;
    const currentCopy = getLottieTextRowsValue(element);
    const needsUpdate =
      layout.status === 'ready'
        ? Boolean(draft) || currentCopy !== copy
        : !draft ||
          draft.copy !== copy ||
          draft.value !== currentCopy ||
          draft.status !== layout.status ||
          draft.rowNumber !== layout.rowNumber;
    if (needsUpdate) {
      commit(copy, layout);
    }
  }, [
    element,
    layout,
    measurement.status,
    copy,
    source,
    config.parameterKey,
    commit,
  ]);

  const isError = !['ready', 'pending'].includes(layout.status);
  const message = isError
    ? getLottieTextRowsMessage(layout) +
      ' ' +
      __(
        'Your full copy is kept in this session; the preview shows the last fitting version.',
        'web-stories'
      )
    : layout.status === 'pending'
    ? __('Loading template font…', 'web-stories')
    : sprintf(
        __(
          'Use Enter for a new row. Maximum %d rows; each line must fit within the strap.',
          'web-stories'
        ),
        rows.length
      );
  const descriptionId = 'lottie-copy-description-' + element.id;

  return (
    <>
      <TextArea
        aria-label={config.label}
        aria-describedby={descriptionId}
        aria-invalid={isError}
        rows={rows.length}
        wrap="off"
        value={copy}
        containerStyleOverride={inputContainerStyleOverride}
        onChange={(event) => {
          const nextCopy = event.target.value;
          setCopy(nextCopy);
          let nextLayout = { status: measurement.status };
          if (measurement.status === 'ready') {
            try {
              nextLayout = validateLottieTextRows(
                nextCopy,
                rows,
                measurement.measurer.measure,
                config.maxLength
              );
            } catch {
              nextLayout = { status: 'font-error' };
            }
          }
          // Preserve invalid copy before a header Save can run.
          commit(nextCopy, nextLayout);
        }}
      />
      <CopyMessage
        id={descriptionId}
        role={isError ? 'alert' : 'status'}
        size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
      >
        {message}
      </CopyMessage>
      <Button
        aria-label={__('Reset Copy', 'web-stories')}
        variant={BUTTON_VARIANTS.RECTANGLE}
        type={BUTTON_TYPES.TERTIARY}
        size={BUTTON_SIZES.SMALL}
        onClick={() => {
          const update = resetLottieTextRows(element);
          setCopy(getLottieTextRowsSource({ ...element, ...update }));
          pushUpdate(
            (current) =>
              current.id === element.id ? resetLottieTextRows(current) : {},
            true
          );
        }}
      >
        {__('Reset Copy', 'web-stories')}
      </Button>
    </>
  );
}

MultiLineCopyInput.propTypes = {
  element: PropTypes.object.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default MultiLineCopyInput;
